// Shared triage/decision-assessment definition used by the agent checklist
// and the reporter-facing decision & tracking pages.

export const FIELDS = [
  { key: "severity", title: "Apparent severity", type: "single", options: [
    "Critical — imminent or exceptionally serious risk",
    "High — serious potential harm",
    "Moderate — meaningful policy concern",
    "Low — limited impact",
    "Informational / no apparent safety risk",
    "Cannot determine yet",
  ] },
  { key: "harm_category", title: "Harm category", type: "multi", options: [
    "Terrorism or violent extremism",
    "Threats or glorification of violence",
    "Child safety concern",
    "Harassment, bullying or targeted abuse",
    "Hate speech or discriminatory abuse",
    "Self-harm or suicide-related content",
    "Sexual or exploitative content",
    "Fraud, scams or deceptive activity",
    "Privacy or personal information exposure",
    "Illegal goods or regulated activities",
    "Username / identity concern",
    "Other policy violation",
    "No identifiable violation yet",
  ] },
  { key: "verifiable", title: "Is the reported content verifiable?", type: "single", options: [
    "Yes — directly accessible",
    "Partially — some evidence is available",
    "No — content is deleted or inaccessible",
    "Link or identifier is invalid",
    "Insufficient information provided",
    "Not applicable",
  ] },
  { key: "scope", title: "Scope of the issue", type: "single", options: [
    "Single item or account",
    "Multiple items from one account",
    "Multiple accounts or coordinated activity",
    "Potentially widespread issue",
    "Unknown",
  ] },
  { key: "urgent", title: "Urgent escalation required?", type: "single", options: [
    "Yes — immediate escalation warranted",
    "Yes — priority review recommended",
    "No — standard queue is appropriate",
    "Undetermined — further assessment required",
  ] },
  { key: "policy", title: "Policy violation identifiable?", type: "single", options: [
    "Yes — clear potential violation",
    "Possible violation — interpretation required",
    "No violation identified",
    "More context or evidence needed",
  ] },
  { key: "next_step", title: "Recommended next step", type: "single", options: [
    "Escalate to Critical Harms Ops",
    "Escalate to Safety Ops",
    "Request additional information",
    "Conduct further investigation",
    "Proceed with standard moderation review",
    "Refer to the relevant specialist team",
    "Close as unverifiable, with documented reasoning",
    "Prepare an appeal review",
    "Refer outside the team's scope",
  ] },
  { key: "confidence", title: "Assessment confidence", type: "single", options: [
    "High — evidence and policy are clear",
    "Medium — some ambiguity remains",
    "Low — specialist guidance is needed",
  ] },
  { key: "preliminary", title: "Preliminary assessment", type: "single", options: [
    "Potential violation confirmed for further action",
    "No violation identified",
    "Insufficient evidence to determine",
    "Duplicate report",
    "Out of scope",
    "Pending escalation or specialist review",
  ] },
  { key: "duplicate", title: "Is the report a duplicate?", type: "single", options: [
    "Yes, exact duplicate", "Related to an existing case", "No", "Uncertain",
  ] },
  { key: "availability", title: "Is the reported content still available?", type: "single", options: [
    "Available", "Removed", "Restricted", "Unavailable", "Unknown",
  ] },
  { key: "conflict", title: "Conflict of interest?", type: "single", options: [
    "No conflict identified", "Potential conflict", "Confirmed conflict — reassign",
  ] },
];

export const FIELD_TITLE = Object.fromEntries(FIELDS.map((f) => [f.key, f.title]));

export const PRIORITY_META = {
  P1: { label: "P1 — Critical", hint: "Immediate escalation (e.g. Critical Harms Ops)", color: "#ef4444" },
  P2: { label: "P2 — High", hint: "Expedited review for serious/complex safety cases", color: "#f59e0b" },
  P3: { label: "P3 — Standard", hint: "Normal queue for clear, routine cases", color: "#10b981" },
  P4: { label: "P4 — Pending", hint: "Awaiting further evidence, info or specialist review", color: "#3b82f6" },
};

export function computePriority(d = {}) {
  const sev = d.severity || "";
  const urg = d.urgent || "";
  const next = d.next_step || "";
  const prelim = d.preliminary || "";
  const ver = d.verifiable || "";
  if (sev.startsWith("Critical") || urg.startsWith("Yes — immediate") || next === "Escalate to Critical Harms Ops") return "P1";
  if (sev.startsWith("High") || urg.startsWith("Yes — priority") || next === "Escalate to Safety Ops") return "P2";
  if (
    next === "Request additional information" ||
    prelim === "Insufficient evidence to determine" ||
    prelim === "Pending escalation or specialist review" ||
    ["No — content is deleted or inaccessible", "Insufficient information provided", "Link or identifier is invalid"].includes(ver)
  ) return "P4";
  return "P3";
}
